import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toaster from 'react-hot-toast';
import { useHistory, useLocation } from 'react-router-dom';

type SortDirection = 'asc' | 'desc';
type SortModel = { field: string; sort: SortDirection }[];

interface QueryParams {
  searchInput: string;
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: string;
}

interface UsePaginatedTableQueryOptions<T> {
  queryKey: string;
  queryFn: (params: QueryParams) => Promise<T>;
  errorMessage: string;
  defaultSortModel?: SortModel;
  defaultPageSize?: number;
  onQueryError?: (error: unknown) => void;
}

const DEFAULT_SORT_MODEL: SortModel = [{ field: 'createdAt', sort: 'desc' }];

/**
 * Keeps a paginated table's search/sort/page/pageSize state in sync with the URL query string, so
 * navigating away (e.g. clicking a row) and back via the browser restores the same view. Also
 * wires that state into the react-query call every paginated admin table needs: keepPreviousData,
 * a console.log + toast on failure, and translating the grid's sortModel into the sortBy/sortOrder
 * params the search APIs expect.
 */
export function usePaginatedTableQuery<T>({
  queryKey,
  queryFn,
  errorMessage,
  defaultSortModel = DEFAULT_SORT_MODEL,
  defaultPageSize = 20,
  onQueryError,
}: UsePaginatedTableQueryOptions<T>) {
  const history = useHistory();
  const location = useLocation();

  const [initial] = useState(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get('page') ?? '', 10);
    const pageSize = parseInt(params.get('pageSize') ?? '', 10);
    const sortBy = params.get('sortBy');
    const sortOrder = params.get('sortOrder');
    return {
      pageNumber: Number.isNaN(page) || page < 0 ? 0 : page,
      pageSize: Number.isNaN(pageSize) || pageSize < 1 ? defaultPageSize : pageSize,
      searchInput: params.get('search') ?? '',
      sortModel: (sortBy && (sortOrder === 'asc' || sortOrder === 'desc')
        ? [{ field: sortBy, sort: sortOrder }]
        : defaultSortModel) as SortModel,
    };
  });

  const [pageNumber, setPageNumber] = useState(initial.pageNumber);
  const [pageSize, setPageSize] = useState(initial.pageSize);
  const [searchInput, setSearchInput] = useState(initial.searchInput);
  const [sortModel, setSortModel] = useState<SortModel>(initial.sortModel);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchInput) params.set('search', searchInput);
    if (pageNumber > 0) params.set('page', String(pageNumber));
    if (pageSize !== defaultPageSize) params.set('pageSize', String(pageSize));
    if (sortModel.length > 0) {
      params.set('sortBy', sortModel[0].field);
      params.set('sortOrder', sortModel[0].sort);
    }
    const qs = params.toString();
    if (qs === location.search.replace(/^\?/, '')) return;
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [searchInput, pageNumber, pageSize, sortModel]);

  const handleSearch = (value: string) => {
    setSearchInput(value.trim());
    setPageNumber(0);
  };

  const handleSortModelChange = (newSortModel: SortModel) => {
    setPageNumber(0);
    setSortModel(newSortModel);
  };

  const handlePageChange = ({
    page,
    pageSize: newPageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageNumber(page);
    setPageSize(newPageSize);
  };

  const { data, isLoading } = useQuery(
    [queryKey, { searchInput, pageNumber, pageSize, sortModel }],
    () =>
      queryFn({
        searchInput,
        pageNumber,
        pageSize,
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort?.toUpperCase(),
      }),
    {
      keepPreviousData: true,
      onError: (error: unknown) => {
        onQueryError?.(error);
        const apiError = error as { response?: { data?: { error?: string } } };
        toaster.error(apiError?.response?.data?.error ?? errorMessage);
      },
    },
  );

  return {
    data,
    isLoading,
    pageNumber,
    setPageNumber,
    pageSize,
    searchInput,
    handleSearch,
    sortModel,
    handleSortModelChange,
    handlePageChange,
  };
}
